import { useState } from "react";
import { Avatar, type AvatarShape } from "../../components/Avatar.js";
import { Icon } from "../../components/Icon.js";
import { Callout } from "../../components/Callout.js";
import { fmt, dollarsToCents } from "../../lib/money.js";
import { postTransaction } from "../../api/transactions.js";
import { postTransfer } from "../../api/transfers.js";
import type { ParentBundle } from "../../api/me.js";

export type ActionMode = "add" | "subtract" | "transfer";

export interface ActionModalState {
  type: ActionMode;
  kidId?: string;
}

export interface ActionModalProps {
  modal: ActionModalState;
  onClose: () => void;
  family: ParentBundle["family"];
  onCompleted: () => void | Promise<void>;
}

const TITLES: Record<ActionMode, string> = {
  add: "Add funds",
  subtract: "Subtract funds",
  transfer: "Transfer between kids",
};

function isValidDollarAmount(s: string): boolean {
  if (!s) return false;
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return false;
  return Number(s) > 0;
}

export default function ActionModal({ modal, onClose, family, onCompleted }: ActionModalProps) {
  const initialFrom = modal.kidId ?? family[0]?.user.rowKey ?? "";
  const initialTo =
    family.find((k) => k.user.rowKey !== initialFrom)?.user.rowKey ?? family[1]?.user.rowKey ?? "";

  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [kidId, setKidId] = useState<string>(initialFrom);
  const [toKidId, setToKidId] = useState<string>(initialTo);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const kid = family.find((k) => k.user.rowKey === kidId);
  const toKid = family.find((k) => k.user.rowKey === toKidId);

  if (!kid) return null;

  const amountValid = isValidDollarAmount(amount);
  const amtCents = amountValid ? dollarsToCents(amount) : 0;
  const overdraft = modal.type === "subtract" && amtCents > kid.account.balanceCents;
  const sameKid = modal.type === "transfer" && kidId === toKidId;
  const canSubmit = amountValid && !overdraft && !sameKid && !busy;

  const handlePickKid = (newKidId: string) => {
    setKidId(newKidId);
    if (modal.type === "transfer" && newKidId === toKidId) {
      const next = family.find((k) => k.user.rowKey !== newKidId);
      if (next) setToKidId(next.user.rowKey);
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      if (modal.type === "add") {
        await postTransaction({
          kidId,
          amountCents: amtCents,
          note: note || "Deposit from parent",
          type: "deposit",
        });
      } else if (modal.type === "subtract") {
        await postTransaction({
          kidId,
          amountCents: -amtCents,
          note: note || "Adjustment",
          type: "adjustment",
        });
      } else {
        await postTransfer({
          fromKidId: kidId,
          toKidId,
          amountCents: amtCents,
          note: note || `Transfer from ${kid.user.name} to ${toKid?.user.name ?? "kid"}`,
        });
      }
      await onCompleted();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const renderKidPicker = (
    selectedId: string,
    onPick: (id: string) => void,
    excludeId?: string,
  ) => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
      {family
        .filter((k) => k.user.rowKey !== excludeId)
        .map((k) => {
          const color = k.user.color ?? "var(--brand)";
          const active = selectedId === k.user.rowKey;
          return (
            <button
              key={k.user.rowKey}
              type="button"
              onClick={() => onPick(k.user.rowKey)}
              style={{
                padding: "12px 8px",
                borderRadius: 12,
                background: active ? color : "var(--canvas-warm)",
                color: active ? "white" : "var(--ink)",
                border: "none",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <Avatar
                kid={{
                  name: k.user.name,
                  color,
                  avatarShape: (k.user.avatarShape as AvatarShape) ?? "circle",
                }}
                size={32}
              />
              {k.user.name}
            </button>
          );
        })}
    </div>
  );

  const submitLabel =
    modal.type === "add" ? "Add funds" : modal.type === "subtract" ? "Subtract" : "Send";

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="row-between" style={{ marginBottom: 22 }}>
          <h3>{TITLES[modal.type]}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            <Icon name="close" size={14} />
          </button>
        </div>

        <div className="stack" style={{ gap: 16 }}>
          <div className="input-group">
            <label className="label">{modal.type === "transfer" ? "From" : "Account"}</label>
            {renderKidPicker(kidId, handlePickKid)}
          </div>

          {modal.type === "transfer" && (
            <div className="input-group">
              <label className="label">To</label>
              {renderKidPicker(toKidId, setToKidId, kidId)}
            </div>
          )}

          <div className="input-group">
            <label className="label">Amount</label>
            <div className="row">
              <div style={{ fontSize: 28, fontFamily: "Fraunces, serif" }}>$</div>
              <input
                className="input"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                step="0.01"
                min="0"
                autoFocus
              />
            </div>
            {overdraft && (
              <div style={{ fontSize: 12, color: "var(--neg)" }}>
                That&apos;s more than {kid.user.name} has ({fmt(kid.account.balanceCents)}).
              </div>
            )}
          </div>

          <div className="input-group">
            <label className="label">Note (optional)</label>
            <input
              className="input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Birthday money, Weekend chores"
              maxLength={120}
            />
          </div>

          {modal.type === "transfer" && amountValid && !sameKid && toKid && (
            <Callout iconName="transfer">
              <div style={{ fontSize: 13 }}>
                {fmt(amtCents)} from <b>{kid.user.name}</b> ({fmt(kid.account.balanceCents)} →{" "}
                {fmt(kid.account.balanceCents - amtCents)}) to <b>{toKid.user.name}</b> (
                {fmt(toKid.account.balanceCents)} → {fmt(toKid.account.balanceCents + amtCents)})
              </div>
            </Callout>
          )}

          {error && (
            <div style={{ fontSize: 12, color: "var(--neg)" }} role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="row" style={{ justifyContent: "flex-end", marginTop: 22 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-brand" onClick={submit} disabled={!canSubmit}>
            {busy ? "Working…" : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
